/**
 * Phase 46 (Plan 46-07) — Remove route confirmation modal.
 *
 * Destructive confirm — the body copy varies by scope per UI-SPEC §Surface 2
 * line 211 (global removal is severe — can leave a kind with ZERO global
 * delivery) vs §Surface 1 line 189 (project removal falls back to global).
 */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Btn } from '../ui/Btn';
import {
  EVENT_KIND_LABELS,
  CHANNEL_LABELS,
  type EventKind,
  type Channel,
} from '../../constants/notificationEnums';

export type RemoveScope = 'project' | 'global';

export interface RemoveRouteConfirmModalProps {
  open: boolean;
  scope: RemoveScope;
  eventKind: EventKind | null;
  channel: Channel | null;
  isRemoving?: boolean;
  /**
   * F3-004: prop type widened to accept async callers. The modal awaits the
   * returned promise and surfaces rejections via `removeError` — without this
   * the caller's `deleteMutation.mutateAsync` rejection became an unhandled
   * promise and the dialog stayed open with the spinner stuck.
   */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function RemoveRouteConfirmModal({
  open,
  scope,
  eventKind,
  channel,
  isRemoving = false,
  onConfirm,
  onClose,
}: RemoveRouteConfirmModalProps) {
  // F3-004: surface async rejections in-modal so a 4xx/5xx never leaves the
  // operator with a frozen spinner.
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setRemoveError(null);
      setPending(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setRemoveError(null);
    setPending(true);
    try {
      await onConfirm();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setPending(false);
    }
  };

  if (!eventKind || !channel) {
    return (
      <Modal open={open} onClose={onClose} title="Remove notification route?" footer={null}>
        <p className="text-[12.5px] text-ms-text-secondary">
          No route selected.
        </p>
      </Modal>
    );
  }

  const kindLabel = EVENT_KIND_LABELS[eventKind];
  const channelLabel = CHANNEL_LABELS[channel].label;

  const projectBody = (
    <p className="text-[12.5px] text-ms-text-secondary">
      This will remove the route for{' '}
      <span className="font-semibold text-ms-text-strong">{kindLabel}</span> on{' '}
      <span className="font-semibold text-ms-text-strong">{channelLabel}</span>.
      The project will fall back to the global default for this event kind.
    </p>
  );

  const globalBody = (
    <p className="text-[12.5px] text-ms-text-secondary">
      This will remove the global default for{' '}
      <span className="font-semibold text-ms-text-strong">{kindLabel}</span> on{' '}
      <span className="font-semibold text-ms-text-strong">{channelLabel}</span>.
      Projects without a per-project route will receive NO notification on this
      channel for this event kind. Confirm with care.
    </p>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Remove notification route?"
      footer={
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} disabled={isRemoving || pending}>
            Keep route
          </Btn>
          <Btn
            variant="danger"
            onClick={handleConfirm}
            isLoading={isRemoving || pending}
          >
            Remove route
          </Btn>
        </div>
      }
    >
      <div data-scope={scope} className="space-y-2">
        {scope === 'global' ? globalBody : projectBody}
        {removeError && (
          <p className="text-[12px] text-rose-600" role="alert">
            {removeError}
          </p>
        )}
      </div>
    </Modal>
  );
}
